import { useState, useEffect } from "react"
import { Button } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useAuth } from "../../../contexts/auth"

CompositeField.propTypes = {
  children: PropTypes.any,
  onChange: PropTypes.func,
  onClickAdd: PropTypes.func,
  addLabel: PropTypes.string,
  defaultValue: PropTypes.array,
  value: PropTypes.array,
  maxItems: PropTypes.number,
  isAddDisabled: PropTypes.bool,
  orientation: PropTypes.string
}

export function CompositeField({
  children,
  onChange,
  onClickAdd,
  addLabel,
  defaultValue = [],
  value,
  maxItems,
  isAddDisabled = false,
  orientation = "BOTTOM"
}) {
  const [state, setState] = useState({
    items: Array.from(defaultValue),
    maxReached: maxItems && (defaultValue.length >= maxItems)
  })

  useEffect(() => {
    if (value) {
      setState({ ...state, items: Array.from(value) })
    }
  }, [value])

  const handleMaxReached = items => {
    if (typeof maxItems !== "undefined") {
      if (items.length >= maxItems) {
        setState({ ...state, maxReached: true })
        return true
      }
      else if (state.maxReached) {
        setState({ ...state, maxReached: false })
        return false
      }
    }
  }

  const isMaxReached = count => {
    if (typeof maxItems === "undefined") {
      return false
    }

    return count >= maxItems
  }

  const addNew = value => {
    const newItems = [...state.items, value]

    setState({ items: newItems, maxReached: isMaxReached(newItems.length) })
    handleChange(newItems, value)
  }

  /**
   * @param {(newItems: any[], setNewItems: callback) => any} handleWithCustomLogic will perform
   * @param copiedItem
   * @param {number} originalItemIdxPosition
   * @param {'below' | 'bottom'} target
   */
  const duplicateItem = ({ copiedItem, originalItemIdxPosition, target = 'below', handleWithCustomLogic }) => {
    const newItems = [...state.items]

    if (handleWithCustomLogic) {
      const setNewItems = (items) => setState(prevState => {
        handleChange(items, value)
        return {
          ...prevState,
          items,
          maxReached: isMaxReached(items.length)
        }
      })
      return handleWithCustomLogic(newItems, setNewItems)
    }

    if (target === 'below') {
      newItems.splice(originalItemIdxPosition + 1, 0, copiedItem)
    } else {
      newItems.push(copiedItem)
    }

    setState({ items: newItems, maxReached: isMaxReached(newItems.length) })
    handleChange(newItems, value)
  }

  const updateItem = index => {
    return event => {
      const value = event.currentTarget?.value || event.target?.value
      const name = event.currentTarget?.id || event.target?.name
      const newItems = Array.from(state.items)
      newItems[index][name] = value


      const itemCopy = {...newItems[index]};

      itemCopy[name] = value;
      
      newItems[index] = itemCopy;

      setState({ ...state, items: newItems })
      handleChange(newItems)
    }
  }

  const updateItemGobernanza = index => {
    return event => {
      const value = event.currentTarget?.value || event.target?.value
      const name = event.currentTarget?.id || event.target?.name
      const newItems = Array.from(state.items)

      const itemCopy = {...newItems[index]};

      itemCopy[name] = value;
      
      newItems[index] = itemCopy;

      setState({ ...state, items: newItems })
      handleChange(newItems)
    }
  }

  const onFilesChange = (index, documents, name) => {
    const newItems = Array.from(state.items)
    newItems[index][name] = documents

    setState({ ...state, items: newItems })
    handleChange(newItems)
  }

  const replaceItemAtIndex = (index, data) => {
    const newItems = Array.from(state.items)
    newItems[index] = data
    setState({ ...state, items: newItems })
    handleChange(newItems)
  }

  const removeItem = index => {
    return event => {
      const newItems = Array.from(state.items)?.filter((it, i) => i !== index)
      setState({ items: newItems, maxReached: isMaxReached(newItems.length) })
      handleChange(newItems)
    }
  }

  const handleChange = (items, editedValue) => {
    onChange && onChange(items, editedValue)
  }

  
  const auth = useAuth() || {}
  const { user } = auth
  const userAuth = user?.claims?.role === "ADMIN_AUDITOR"

  const renderButtonDashed = () => (
    <Button
      type="dashed"
      style={{
        color: "#612F5E",
      }}
      icon={<PlusOutlined />}
      onClick={() => onClickAdd && onClickAdd(addNew)}
      //disabled={state.maxReached}
      disabled={userAuth}
      block>
      {addLabel}
    </Button>
  )

  return (
    <div>
      {orientation === "TOP" && (!isAddDisabled && renderButtonDashed())}

      {
        children({
          items: state.items,
          onFilesChange,
          updateItem,
          updateItemGobernanza,
          addNew,
          removeItem,
          replaceItemAtIndex,
          duplicateItem
        })
      }

      {orientation === "BOTTOM" && (!isAddDisabled && renderButtonDashed())}
    </div>
  )
}
